import * as React from "react";
import {
  CheckIcon,
  XMarkIcon,
  ShoppingCartIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  MinusIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserIcon,
  CurrencyDollarIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  HomeIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  ChatBubbleLeftIcon,
  Bars3Icon,
  Squares2X2Icon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  MoonIcon,
  SunIcon,
  // Additional missing icons
  GlobeAltIcon,
  SpeakerWaveIcon,
  ChevronUpDownIcon,
  CreditCardIcon,
  ShoppingBagIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
  PlayIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  EllipsisHorizontalIcon,
  UsersIcon,
  ArchiveBoxIcon,
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon,
  XCircleIcon,
  ArrowLeftIcon,
  CloudArrowUpIcon,
  LinkIcon,
  PhotoIcon,
  ArrowTrendingDownIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";

import { cn } from "@/lib/utils";

// Icon mapping object - maps string keys to actual icon components
const iconMap = {
  check: CheckIcon,
  close: XMarkIcon,
  x: XMarkIcon,
  shoppingCart: ShoppingCartIcon,
  cart: ShoppingCartIcon,
  pencil: PencilIcon,
  edit: PencilIcon,
  trash: TrashIcon,
  delete: TrashIcon,
  refresh: ArrowPathIcon,
  reload: ArrowPathIcon,
  search: MagnifyingGlassIcon,
  filter: FunnelIcon,
  plus: PlusIcon,
  add: PlusIcon,
  minus: MinusIcon,
  remove: MinusIcon,
  eye: EyeIcon,
  view: EyeIcon,
  chevronDown: ChevronDownIcon,
  chevronUp: ChevronUpIcon,
  chevronLeft: ChevronLeftIcon,
  chevronRight: ChevronRightIcon,
  user: UserIcon,
  profile: UserIcon,
  currency: CurrencyDollarIcon,
  dollar: CurrencyDollarIcon,
  warning: ExclamationCircleIcon,
  alert: ExclamationCircleIcon,
  shield: ShieldCheckIcon,
  security: ShieldCheckIcon,
  lock: LockClosedIcon,
  home: HomeIcon,
  settings: Cog6ToothIcon,
  cog: Cog6ToothIcon,
  document: DocumentTextIcon,
  file: DocumentTextIcon,
  chat: ChatBubbleLeftIcon,
  message: ChatBubbleLeftIcon,
  menu: Bars3Icon,
  bars: Bars3Icon,
  grid: Squares2X2Icon,
  logout: ArrowRightOnRectangleIcon,
  signOut: ArrowRightOnRectangleIcon,
  bell: BellIcon,
  notification: BellIcon,
  moon: MoonIcon,
  sun: SunIcon,
  // Additional missing icons
  globe: GlobeAltIcon,
  speaker: SpeakerWaveIcon,
  chevronUpDown: ChevronUpDownIcon,
  creditCard: CreditCardIcon,
  shoppingBag: ShoppingBagIcon,
  trendingUp: ArrowTrendingUpIcon,
  clock: ClockIcon,
  documentCheck: DocumentCheckIcon,
  checkCircle: CheckCircleIcon,
  play: PlayIcon,
  calendar: CalendarIcon,
  download: ArrowDownTrayIcon,
  moreHorizontal: EllipsisHorizontalIcon,
  users: UsersIcon,
  archive: ArchiveBoxIcon,
  externalLink: ArrowTopRightOnSquareIcon,
  copy: DocumentDuplicateIcon,
  xCircle: XCircleIcon,
  arrowLeft: ArrowLeftIcon,
  upload: CloudArrowUpIcon,
  link: LinkIcon,
  photo: PhotoIcon,
  trendingDown: ArrowTrendingDownIcon,
  info: InformationCircleIcon,
  information: InformationCircleIcon,
} as const;

// Type for valid icon names - provides autocompletion and type safety
export type IconName = keyof typeof iconMap;

export interface IconProps {
  name: IconName;
  className?: string;
}

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  ({ name, className }, ref) => {
    const IconComponent = iconMap[name];
    
    if (!IconComponent) {
      return null;
    }
    
    return (
      <IconComponent
        ref={ref}
        className={cn("h-5 w-5", className)}
      />
    );
  }
);

Icon.displayName = "Icon";

export { iconMap };